import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetShowContacts, apiCreateShowContact, apiUpdateShowContact, apiDeleteShowContact,
  type ShowContactDTO,
} from '../api';
import { useConfig } from '../hooks/useConfig';
import { useWriteGate } from '../lib/writeGate';

// Contact persons for a show day — the "CONTACT PERSONS" block of the SAMAEL
// itinerary (stage manager, backstage/hospitality, runners…). The role
// suggestions come from an editable per-band list (config.rider.contactRoles),
// with a fallback preset list.

const FALLBACK_ROLES = ['Stage manager', 'Backstage / hospitality', 'Runner', 'Artist liaison', 'Promoter', 'Driver', 'Security', 'Other'];

interface Props {
  realId: string | null;
  /** The show's band — feeds the capability-aware write gate (lot A2). */
  bandId?: string | null;
  /** Report "this section has content" upward, so the enclosing foldable
   *  section can open itself (LOT 1, 2026-11-04 — same contract as
   *  ShowNotesSection / TransportSection). */
  onHasDataChange?: (has: boolean) => void;
}

export default function ContactPersonsSection({ realId, bandId, onHasDataChange }: Props) {
  const { t } = useTranslation();
  const { config, updateRider } = useConfig();
  // Capability-aware write gate (lot A2). `POST|PUT|DELETE /api/shows/:id/contacts`
  // (server/index.js:616) and the per-band role suggestions written through
  // `PUT /api/bands/:id/rider` (server/index.js:540) are both mounted AFTER the
  // global read-only
  // guard → 403 for a `users.role = 'read-only'` account.
  const writeGate = useWriteGate(bandId);
  const canWrite = writeGate.canWrite;
  const [contacts, setContacts] = useState<ShowContactDTO[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [newRole, setNewRole] = useState('');

  // Report "this section has content" upward without re-subscribing the effect
  // to a callback identity: the ref always holds the latest one.
  const onHasDataRef = useRef(onHasDataChange);
  onHasDataRef.current = onHasDataChange;
  const hasData = contacts.length > 0;
  useEffect(() => { onHasDataRef.current?.(hasData); }, [hasData]);

  // Editable per-band role suggestions (config.rider.contactRoles).
  const rolePresets = config.rider?.contactRoles?.length
    ? config.rider.contactRoles
    : FALLBACK_ROLES;

  const addRole = () => {
    if (!canWrite) return; // config.rider write → server 403 for a read-only account
    const r = newRole.trim();
    if (!r) return;
    const current = config.rider?.contactRoles?.length ? config.rider.contactRoles : FALLBACK_ROLES;
    if (current.includes(r)) { setNewRole(''); return; }
    updateRider({ contactRoles: [...current, r] });
    setNewRole('');
  };

  const removeRole = (role: string) => {
    if (!canWrite) return; // config.rider write → server 403 for a read-only account
    const current = config.rider?.contactRoles?.length ? config.rider.contactRoles : FALLBACK_ROLES;
    updateRider({ contactRoles: current.filter((x) => x !== role) });
  };

  const load = useCallback(async () => {
    if (!realId) return;
    const list = await apiGetShowContacts(realId).catch(() => []);
    setContacts([...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    setLoaded(true);
  }, [realId]);

  useEffect(() => { void load(); }, [load]);

  const addContact = async () => {
    if (!canWrite) return; // frozen for a global read-only account (server 403s)
    if (!realId) return;
    const item = await apiCreateShowContact(realId, {
      role: '', name: '', phone: '', email: '', notes: '', sort_order: contacts.length,
    });
    setContacts((p) => [...p, item]);
  };

  const updateContact = async (c: ShowContactDTO, patch: Partial<ShowContactDTO>) => {
    if (!canWrite) return; // frozen for a global read-only account (server 403s)
    if (!realId) return;
    const updated = await apiUpdateShowContact(realId, c.id, { ...c, ...patch });
    setContacts((p) => p.map((x) => (x.id === updated.id ? updated : x)));
  };

  const removeContact = async (c: ShowContactDTO) => {
    if (!canWrite) return; // frozen for a global read-only account (server 403s)
    if (!realId) return;
    await apiDeleteShowContact(realId, c.id);
    setContacts((p) => p.filter((x) => x.id !== c.id));
  };

  return (
    <div>
      <h4 style={{ margin: '0 0 8px' }}>{t('contactPersons.title')}</h4>
      <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', margin: '0 0 8px' }}>
        {t('contactPersons.hint')}
      </p>
      {loaded && contacts.length === 0 ? (
        <p style={{ color: 'var(--text-dim)', margin: '0 0 8px' }}>{t('contactPersons.noContacts')}</p>
      ) : null}
      <div className="rc-data-block contact-rows">
        {contacts.map((c) => (
          <div key={c.id} className="contact-row" style={{
            /* Grille portée par CSS (.contact-row dans index.css) : un
               display inline battrait la règle et la rangée resterait en flex. */
            padding: 8, border: '1px solid var(--border)', borderRadius: 8,
          }}>
            <input
              className="form-input"
              style={{ width: 150 }}
              list="contact-role-presets"
              value={c.role || ''}
              placeholder={t('contactPersons.rolePh')}
              disabled={!canWrite}
              onChange={(e) => updateContact(c, { role: e.target.value })}
            />
            <datalist id="contact-role-presets">
              {rolePresets.map((r) => <option key={r} value={r} />)}
            </datalist>
            <input className="form-input" style={{ flex: '1 1 120px', minWidth: 110 }} value={c.name || ''}
              placeholder={t('contactPersons.namePh')} disabled={!canWrite} onChange={(e) => updateContact(c, { name: e.target.value })} />
            <input className="form-input" style={{ width: 150 }} value={c.phone || ''}
              placeholder={t('contactPersons.phonePh')} disabled={!canWrite} onChange={(e) => updateContact(c, { phone: e.target.value })} />
            <input className="form-input" style={{ flex: '1 1 140px', minWidth: 130 }} value={c.email || ''}
              placeholder={t('contactPersons.emailPh')} disabled={!canWrite} onChange={(e) => updateContact(c, { email: e.target.value })} />
            <input className="form-input" style={{ flex: '1 1 120px', minWidth: 100 }} value={c.notes || ''}
              placeholder={t('contactPersons.notesPh')} disabled={!canWrite} onChange={(e) => updateContact(c, { notes: e.target.value })} />
            <button className="btn btn-secondary" onClick={() => removeContact(c)} disabled={!canWrite}>×</button>
          </div>
        ))}
      </div>
      <button className="btn btn-primary" onClick={addContact} disabled={!canWrite} style={{ marginTop: 8 }}>{t('contactPersons.addContact')}</button>

      {/* Editable role list — suggestions for the whole band's contact persons.
          LOT U2d : ce bloc rejoint l'axe de la GRILLE DE PAGE (`.rc-data-block`
          porte le retrait calculé pour que le 1er contrôle tombe sur l'ancre,
          x=396 à 1440) et la ligne « Add a role… » démarre sur une rangée À
          ELLE (`flexBasis: 100%`) : avant, l'input suivait les chips dans le
          flux et atterrissait à x=1031, seul contrôle hors grille de la page. */}
      <div className="rc-data-block" style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <label style={{ display: 'block', fontSize: 'var(--font-sm)', color: 'var(--text-dim)', marginBottom: 6 }}>
          {t('contactPersons.roleSuggestions')}
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {rolePresets.map((r) => (
            <span key={r} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '2px 8px', background: 'var(--accent-muted)', borderRadius: 999,
              fontSize: 'var(--font-xs)',
            }}>
              {r}
              <button type="button" aria-label={t('contactPersons.removeRole', { role: r })}
                onClick={() => removeRole(r)} disabled={!canWrite}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, fontSize: 12 }}>×</button>
            </span>
          ))}
          {/* `paddingLeft: 8` = le cadre des rangées du bloc (`.contact-row` a un
              padding de 8) : sans lui le contrôle tombait à x387, soit 8 px à
              gauche de l'ancre des autres champs (x395). */}
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexBasis: '100%', paddingLeft: 8 }}>
            <input
              className="form-input"
              style={{ width: 150 }}
              value={newRole}
              placeholder={t('contactPersons.addRolePh')}
              disabled={!canWrite}
              onChange={(e) => setNewRole(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRole(); } }}
            />
            <button className="btn btn-secondary btn-small" onClick={addRole} disabled={!canWrite}>+</button>
          </span>
        </div>
      </div>
    </div>
  );
}
